import { useEffect, useState } from 'react'
import { Navigate, useLocation, useParams } from 'react-router'
import { Button, ButtonLink, EmptyState, ExplainerBanner, Skeleton } from '@/ds'
import { useStay } from '@/app/stay/StayProvider'

import { useAppNavigate, withQuery } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { reservaService } from '@/services/reserva'
import type { Stay } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { PendingChangeBanner } from './PendingChangeBanner'
import { BOOKING_ROUTES, dayOf, longRange, TYPE_BY_SLUG, useBookingStay, useLang, useChangeExit } from './shared'
import { whatsappHref } from '@/lib/whatsapp'

type Cause = 'rejected' | 'card' | 'generic' | 'already_paid' | 'offline'

/**
 * La reversión se lanza una sola vez por cambio aunque la pantalla se monte dos veces (StrictMode o recarga del
 * proveedor): E12 no es idempotente y una segunda llamada fallaría por no haber cambio pendiente.
 */
const reverts = new Map<string, Promise<Stay>>()
function revertOnce(stay: Stay, changeId: string, lang: string): Promise<Stay> {
  let promise = reverts.get(changeId)
  if (!promise) {
    promise = reservaService.revertChange(stay, changeId, lang)
    reverts.set(changeId, promise)
  }
  return promise
}

/**
 * Mitigación de D45: el cambio ya está aplicado (E12) y su cobro no se ha completado (fallo al empezar o rechazo en el
 * banco). Se intenta volver a las fechas originales con E12 y se explica. Si no se puede, queda "cambio pendiente de
 * pago" con la acción de pagar. Sin pantalla en el bundle.
 */
export function SinPago() {
  const { tipo = '' } = useParams()
  const type = TYPE_BY_SLUG[tipo]
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useBookingStay()
  const { replaceStay } = useStay()
  const { go, search } = useAppNavigate()
  const back = useChangeExit()
  const routeState = useLocation().state as { changeId?: string; cause?: Cause } | null
  const changeId = routeState?.changeId ?? null
  const cause: Cause = routeState?.cause ?? 'rejected'
  const [originalStay] = useState(stay)

  const [revert] = useAsync(`revertir:${stay.guestCode}:${changeId ?? ''}`, () => (changeId ? revertOnce(originalStay, changeId, apiLang) : Promise.resolve(null)))
  const failed = revert.kind === 'error' || revert.kind === 'unsupported'
  // Sin reversión (fallida o sin cambio en curso): lo que diga el registro del cambio pendiente y E2.
  const [pending] = useAsync(`pendiente:${stay.guestCode}:${failed || !changeId ? 'si' : 'no'}`, (signal) => (failed || !changeId ? reservaService.getPendingChange(stay, apiLang, signal) : Promise.resolve(null)))

  useEffect(() => {
    if (revert.kind === 'ready' && revert.value) replaceStay(revert.value)
    // La reserva que ahora tiene el PMS es la cambiada: se relee para que Reserva y Tu estancia la muestren.
    if (failed) reservaService.getStay(stay.guestCode, apiLang).then(replaceStay, () => undefined)
    // Solo cuando termina la reversión.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revert.kind])

  if (!type) return <Navigate to={withQuery(BOOKING_ROUTES.detail, search)} replace />

  const causeText = cause === 'rejected' ? t('v2.reserva.sinPago.causa.rechazado') : cause === 'offline' ? t('v2.ds.offline') : cause === 'card' ? t('v2.legacy.payment.error.Payment_failed.description') : t('v2.reserva.sinPago.causa.generico')
  const contact = stay.contact.whatsapp ? (
    <ButtonLink variant="secondary" icon="message-circle" href={whatsappHref(stay.contact.whatsapp, stay.reference)} target="_blank" rel="noreferrer">
      {t('v2.ds.contact.whatsapp')}
    </ButtonLink>
  ) : null

  let body: React.ReactNode
  if (changeId && revert.kind === 'loading') {
    body = (
      <div aria-busy="true" className="pagos__stack">
        <p className="page__lead" role="status">
          {t('v2.reserva.sinPago.revirtiendo')}
        </p>
        <Skeleton height={140} />
      </div>
    )
  } else if (revert.kind === 'ready' && revert.value) {
    const reverted = revert.value
    body = (
      <>
        <ExplainerBanner tone="info" title={t('v2.reserva.sinPago.revertidoTitulo')}>
          {`${causeText} ${t('v2.reserva.sinPago.revertidoTexto', { range: longRange(dayOf(reverted.dates.checkIn), dayOf(reverted.dates.checkOut), lang, t) })}`}
        </ExplainerBanner>
        <div className="pagos__group">
          <Button onClick={() => go(BOOKING_ROUTES.pick(type), { replace: true })}>{t('v2.comun.reintentar')}</Button>
          <Button variant="secondary" onClick={() => back.exit({ replace: true })}>
            {back.returnLabel}
          </Button>
        </div>
      </>
    )
  } else if (pending.kind === 'loading') {
    body = (
      <div aria-busy="true">
        <Skeleton height={180} />
      </div>
    )
  } else if (pending.kind === 'ready' && pending.value) {
    body = (
      <>
        <p className="page__lead">{`${causeText} ${t('v2.reserva.sinPago.noRevertido')}`}</p>
        <PendingChangeBanner pending={pending.value} />
        <div className="pagos__group">
          {contact}
          {/* Cambio pendiente de pago: a Tu reserva, que lo muestra con "Pagar", sea cual sea el origen. */}
          <Button variant="secondary" onClick={() => go(BOOKING_ROUTES.detail, { replace: true, query: { volver: null } })}>
            {t('v2.reserva.noDisponible.volver')}
          </Button>
        </div>
      </>
    )
  } else {
    body = (
      <EmptyState icon="calendar" title={t('v2.reserva.listo.sinCambio')} headingLevel={2} actionLabel={back.returnLabel} onAction={() => back.exit({ replace: true })}>
        {t('v2.reserva.listo.sinCambioTexto')}
      </EmptyState>
    )
  }

  return (
    <main className="page reserva">
      <div className="pagos__head">
        <h1>{t('v2.reserva.sinPago.titulo')}</h1>
      </div>
      {body}
    </main>
  )
}
